import { useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Package, Truck, ExternalLink, AlertCircle, MessageCircle } from "lucide-react";
import http from "@/lib/api";
import { toast } from "sonner";
import { openVadimChat } from "@/components/VadimChat";

function formatDate(d) { try { return new Date(d).toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" }); } catch { return d; } }

function ShipmentResult({ s }) {
  return (
    <Card className="border-[#D4A574]/30 shadow-md" data-testid="shipment-result">
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-center gap-3">
          <Truck className="h-6 w-6 text-[#D4A574]" />
          <div>
            <div className="text-xs text-[#1A1A1A]/60">Sporingsnummer</div>
            <div className="font-mono font-semibold text-[#3D2314]" data-testid="shipment-number">{s.tracking_number}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div><div className="text-xs text-[#1A1A1A]/60">Transportør</div><div data-testid="shipment-carrier">{s.carrier || "—"}</div></div>
          <div><div className="text-xs text-[#1A1A1A]/60">Status</div><div className="font-semibold text-[#3D2314]" data-testid="shipment-status">{s.status_label}</div></div>
          <div><div className="text-xs text-[#1A1A1A]/60">Forventet levering</div><div data-testid="shipment-eta">{s.eta_days ? `ca. ${s.eta_days} dager` : "Ikke oppgitt"}</div></div>
        </div>
        {s.last_updated && <p className="text-xs text-[#1A1A1A]/50">Sist oppdatert: {formatDate(s.last_updated)}</p>}
        {s.carrier_tracking_url && (
          <a href={s.carrier_tracking_url} target="_blank" rel="noreferrer" data-testid="shipment-carrier-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#3D2314] underline decoration-[#D4A574]">
            Se sporing hos transportøren <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </CardContent>
    </Card>
  );
}

export default function Sporing() {
  const [tn, setTn] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async () => {
    const v = tn.replace(/\s+/g, "");
    if (v.length < 6) { toast.error("Skriv inn et gyldig sporingsnummer"); return; }
    setLoading(true); setResult(null); setNotFound(false);
    try {
      const res = await http.post("/v1/public/shipment", { tracking_number: v });
      if (res.data?.found) setResult(res.data); else setNotFound(true);
    } catch (e) {
      if (e?.response?.status === 429) toast.error("For mange søk. Vent litt og prøv igjen."); else setNotFound(true);
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="bg-[#FFF8F0] min-h-screen">
        <section className="bg-gradient-to-b from-[#3D2314] to-[#5a3520] text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Package className="h-10 w-10 mx-auto mb-3 text-[#D4A574]" />
            <h1 className="text-3xl md:text-4xl font-bold mb-4 font-display">Spor sending</h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">Skriv inn sporingsnummeret fra transportøren for å se hvor pakken er.</p>
          </div>
        </section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
          <Card className="border-[#D4A574]/30 shadow-md">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#D4A574]" />
                  <Input placeholder="Sporingsnummer (f.eks. 70730259304981234)" value={tn} onChange={(e) => setTn(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()} className="pl-10 border-[#D4A574]/30 bg-white" data-testid="track-input" />
                </div>
                <Button onClick={handleSearch} disabled={loading} className="bg-[#3D2314] hover:bg-[#5a3520] text-white px-8" data-testid="track-search">{loading ? "Søker..." : "Søk"}</Button>
              </div>
            </CardContent>
          </Card>
          {notFound && (
            <div className="flex items-start gap-3 rounded-lg border border-[#D4A574]/40 bg-white p-4 text-sm" data-testid="track-not-found">
              <AlertCircle className="h-5 w-5 text-[#D4A574] shrink-0" />
              <span>Vi fant ingen sending med dette nummeret. Kontroller nummeret og prøv igjen.</span>
            </div>
          )}
          {result && <ShipmentResult s={result} />}
          <div className="rounded-lg bg-white/70 border border-[#D4A574]/30 p-4 text-sm text-[#1A1A1A]/70 flex items-start gap-3" data-testid="sporing-repair-hint">
            <MessageCircle className="h-5 w-5 text-[#D4A574] shrink-0" />
            <div>
              Lurer du på status for reparasjonen din? Spør Vadim i chatten – av hensyn til personvernet bekrefter vi først identiteten din med en engangskode.
              <button onClick={() => openVadimChat("text")} className="block mt-1 underline text-[#3D2314]" data-testid="sporing-open-chat">Åpne chat med Vadim</button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
